import React, { useCallback, useMemo, useRef, useState } from 'react'
import { App, Button, Flex, Modal, Segmented, Typography } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AgGridReact } from 'ag-grid-react'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper/PageHeaderWrapper'
import { parseDate } from '../../../config/dayjsConfig'
import { useCtxReconciliationMatches } from '../../../features/icReconciliation/queries/useCtxReconciliationMatches'
import { useCtxGroupCompany } from '../../../features/companyGroup/queries/useCtxGroupCompany'
import { useFiscalYearOptions } from '../../../features/fiscalYear/queries/useFiscalYearOptions'
import { currentFiscalYearSelector } from '../../../features/fiscalYear/selectors/selectors'
import { useCtxSubsidiaries } from '../../../features/companyGroup/queries/useSubsidiaries'
import { useMappingStats } from '../../../features/groupCustomerMapping/queries/useMappingStats'
import { useGroupUsers } from '../../../features/users/queries/useGroupUsers'
import { exportReconciliationToExcel } from '../../../features/icReconciliation/utils/exportReconciliation'
import { isAccepted } from '../../../features/icReconciliation/utils/reconciliationHelpers'
import { useApproveMatches } from '../../../features/icReconciliation/queries/useApproveMatches'
import { useRevokeApprovals } from '../../../features/icReconciliation/queries/useRevokeApprovals'
import { MAX_APPROVALS_PER_REQUEST } from '../../../features/icReconciliation/service/reconciliationService'
import { Match } from '../../../features/icReconciliation/types'
import { Currency } from '../../../types/currency/Currency'
import { Company } from '../../../types/company/Company'
import { flattenGroup } from '../../../features/companyGroup/utils/companyGroupUtils'
import ReconciliationApproveModal from '../../../features/icReconciliation/components/ReconciliationApproveModal'
import ReconciliationSummaryCards from '../../../features/icReconciliation/components/ReconciliationSummaryCards'
import ReconciliationTable from '../../../features/icReconciliation/components/ReconciliationTable'
import ReconciliationEmptyMappingState from '../../../features/icReconciliation/components/ReconciliationEmptyMappingState'
import MappingWarningButton from '../../../features/icReconciliation/components/MappingWarningButton'
import Loading from '../../../shared/components/Loading'

const { Text } = Typography

const IcReconciliation: React.FC = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const gridRef = useRef<AgGridReact<Match>>(null)
  const [exporting, setExporting] = useState(false)
  const [showAccepted, setShowAccepted] = useState(false)
  const [selectedRows, setSelectedRows] = useState<Match[]>([])
  const [approveTarget, setApproveTarget] = useState<Match[] | null>(null)
  // Rows left after the grid's column filters (date range etc.) — null until the grid reports.
  const [filteredRows, setFilteredRows] = useState<Match[] | null>(null)

  const approveMutation = useApproveMatches()
  const revokeMutation = useRevokeApprovals()

  const { data: groupCompany, isLoading: isGroupLoading } = useCtxGroupCompany()
  const { data: subsidiaries = [], isLoading: isSubsidiariesLoading } = useCtxSubsidiaries()
  const { data: matches = [], isFetching: isMatchesFetching, refetch: refetchMatches } = useCtxReconciliationMatches()

  // Default the grid's date filter to the group parent's ongoing fiscal year.
  const { data: currentFiscalYear } = useQuery(useFiscalYearOptions(groupCompany?.id ?? '', currentFiscalYearSelector))
  const defaultDateRange = useMemo(() => {
    if (!currentFiscalYear) return undefined
    const from = parseDate(currentFiscalYear.startDate)?.format('YYYY-MM-DD')
    const to = parseDate(currentFiscalYear.endDate)?.format('YYYY-MM-DD')
    return from && to ? { from, to } : undefined
  }, [currentFiscalYear])

  const {
    stats: mappingStats,
    rows: mappingRows,
    isCompaniesLoading,
    isSourceMapsLoading: isMappingSourceMapsLoading,
    isError: isMappingError
  } = useMappingStats()

  const hasAnyMappingLink = useMemo(
    () => mappingRows.some(row => row.cells.some(cell => !cell.isSelf && (cell.customer || cell.supplier))),
    [mappingRows]
  )
  // Only treat "no links" as a real configuration gap when the mapping data actually loaded —
  // a failed fetch leaves stats/rows empty and would otherwise trip the blocking modal.
  const isMappingUnconfigured = !isMappingError && mappingStats.total > 0 && !hasAnyMappingLink
  const isMappingDataLoading = isCompaniesLoading || isMappingSourceMapsLoading

  // Acceptance filtering is frontend-owned. Default view hides accepted entries but keeps
  // stale ones (they need action again); the toggle browses accepted entries instead.
  const acceptedMatches = useMemo(() => matches.filter(isAccepted), [matches])
  const openMatches = useMemo(() => matches.filter(match => !isAccepted(match)), [matches])
  const visibleMatches = showAccepted ? acceptedMatches : openMatches
  // What the grid actually shows (acceptance split + column filters) — feeds the cards and export.
  const displayedMatches = filteredRows ?? visibleMatches

  // Flattened recursively so companies nested under a subgroup (a subsidiary that is itself
  // a group with its own subsidiaries) are included, not just the direct subsidiaries.
  const allCompanies = useMemo(
    () => (groupCompany ? flattenGroup({ ...groupCompany, subsidiaries } as Company) : []),
    [groupCompany, subsidiaries]
  )

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    allCompanies.forEach(company => map.set(company.id, company.name))
    return map
  }, [allCompanies])

  // Approver of a Match can be a user from any company in the group, not just the one
  // currently in context — so users are fetched per group company and merged (see
  // useGroupUsers: there's no single bulk "users for this group" endpoint).
  const groupCompanyIds = useMemo(() => allCompanies.map(company => company.id), [allCompanies])
  const groupUsers = useGroupUsers(groupCompanyIds)
  const userNameById = useMemo(() => {
    const map = new Map<number, string>()
    groupUsers.forEach(user => map.set(user.id, user.displayName || user.email))
    return map
  }, [groupUsers])

  // Reporting currency for the whole page — the group parent company's currency.
  const reportingCurrency = groupCompany?.currency ?? Currency.EUR

  const deselectAll = useCallback(() => gridRef.current?.api.deselectAll(), [])

  const handleRevoke = useCallback(
    (rows: Match[]) => {
      if (rows.length === 0) return
      Modal.confirm({
        title: t('icReconciliation:revokeConfirmTitle', 'Peru hyväksyntä?'),
        content: t('icReconciliation:revokeConfirmContent', '{{count}} riviä palautuu käsiteltäväksi.', {
          count: rows.length
        }),
        okText: t('icReconciliation:revokeConfirmOk', 'Peru hyväksyntä'),
        okButtonProps: { danger: true },
        cancelText: t('icReconciliation:approveCancel', 'Peruuta'),
        onOk: () =>
          new Promise<void>(resolve => {
            revokeMutation.mutate(rows, {
              onSettled: () => {
                deselectAll()
                resolve()
              }
            })
          })
      })
    },
    [t, revokeMutation, deselectAll]
  )

  const handleApproveEntry = useCallback((match: Match) => setApproveTarget([match]), [])
  const handleRevokeEntry = useCallback((match: Match) => handleRevoke([match]), [handleRevoke])

  const handleApproveConfirm = (comment: string) => {
    if (!approveTarget) return
    approveMutation.mutate(
      { matches: approveTarget, comment: comment || undefined },
      {
        onSettled: () => {
          setApproveTarget(null)
          deselectAll()
        }
      }
    )
  }

  const overBatchLimit = selectedRows.length > MAX_APPROVALS_PER_REQUEST

  const handleExport = async () => {
    setExporting(true)
    try {
      await exportReconciliationToExcel(
        displayedMatches,
        companyNameById,
        userNameById,
        reportingCurrency,
        `${t('icReconciliation:exportFileName', 'täsmäytys')}-${dayjs().format('YYYY-MM-DD')}`,
        t
      )
    } catch {
      message.error(t('icReconciliation:exportError', 'Vientiä ei voitu luoda'))
    } finally {
      setExporting(false)
    }
  }

  if (isGroupLoading || isSubsidiariesLoading) return <Loading />

  return (
    <PageHeaderWrapper>
      <Flex gap={16} vertical>
        <ReconciliationEmptyMappingState open={!isMappingDataLoading && isMappingUnconfigured} />
        <ReconciliationApproveModal
          open={!!approveTarget}
          count={approveTarget?.length ?? 0}
          loading={approveMutation.isPending}
          onCancel={() => setApproveTarget(null)}
          onConfirm={handleApproveConfirm}
        />
        <ReconciliationSummaryCards matches={displayedMatches} groupCurrency={reportingCurrency} />
        <Flex justify="end" align="center" gap={8}>
          <Segmented
            value={showAccepted ? 'accepted' : 'open'}
            onChange={value => {
              setFilteredRows(null)
              setShowAccepted(value === 'accepted')
            }}
            options={[
              {
                label: `${t('icReconciliation:viewOpen', 'Avoimet')} (${
                  showAccepted ? openMatches.length : displayedMatches.length
                })`,
                value: 'open'
              },
              {
                label: `${t('icReconciliation:viewAccepted', 'Hyväksytyt')} (${
                  showAccepted ? displayedMatches.length : acceptedMatches.length
                })`,
                value: 'accepted'
              }
            ]}
          />
          {selectedRows.length > 0 && (
            <>
              <Text strong>
                {t('icReconciliation:selectedCount', '{{count}} valittu', { count: selectedRows.length })}
              </Text>
              {showAccepted ? (
                <Button danger loading={revokeMutation.isPending} onClick={() => handleRevoke(selectedRows)}>
                  {t('icReconciliation:bulkRevoke', 'Peru hyväksyntä')}
                </Button>
              ) : (
                <>
                  <Button
                    type="primary"
                    disabled={overBatchLimit}
                    loading={approveMutation.isPending}
                    onClick={() => setApproveTarget(selectedRows)}
                  >
                    {t('icReconciliation:bulkApprove', 'Hyväksy valitut')}
                  </Button>
                  {overBatchLimit && (
                    <Text type="danger">
                      {t('icReconciliation:maxBatch', 'Valitse enintään {{max}} kerralla', {
                        max: MAX_APPROVALS_PER_REQUEST
                      })}
                    </Text>
                  )}
                </>
              )}
              <Button type="text" onClick={deselectAll}>
                {t('icReconciliation:clearSelection', 'Tyhjennä valinta')}
              </Button>
            </>
          )}
          <Flex align="center" gap={8} style={{ marginLeft: 'auto' }}>
            {!isMappingDataLoading && <MappingWarningButton missingCount={mappingStats.missing} />}
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchMatches()}
              loading={isMatchesFetching}
              title={t('icReconciliation:filtersRefresh', 'Päivitä')}
            />
            <Button icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
              Excel
            </Button>
          </Flex>
        </Flex>
        <ReconciliationTable
          ref={gridRef}
          matches={visibleMatches}
          companyNameById={companyNameById}
          userNameById={userNameById}
          groupCurrency={reportingCurrency}
          onSelectionChange={setSelectedRows}
          onFilteredRowsChange={setFilteredRows}
          onApproveEntry={handleApproveEntry}
          onRevokeEntry={handleRevokeEntry}
          defaultDateRange={defaultDateRange}
          loading={isMatchesFetching}
        />
      </Flex>
    </PageHeaderWrapper>
  )
}

export default IcReconciliation
